import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ArtemisReportFilterInput,
	type ArtemisReportSortInput,
	CreateReportDocument,
	type CreateReportInput,
	DeleteReportDocument,
	GetReportDocument,
	GetReportsDocument,
	UpdateReportDocument,
	type UpdateReportInput,
} from "@/common/artemis-sdk/graphql";
import type { ArtemisReport, ArtemisReportDetail } from "../model";
import { useGqlClient } from "../use-gql-client";
import { buildWhere, toOrder, usePagedList } from "./use-paged-list";

/**
 * Fetches the first page of reports for a workspace (array form, for lookups).
 * For the paginated list use {@link useReportsPaged}.
 */
export function useReports(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["reports", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetReportsDocument, { workspaceId });
			return (data.reports?.nodes as ArtemisReport[]) ?? [];
		},
		enabled: !!workspaceId,
	});
}

/**
 * Server-side paginated report list: Next/Prev cursor pagination plus server-side
 * search (title/id), type/status filters, and sort.
 */
export function useReportsPaged(workspaceId: string) {
	const gqlFetch = useGqlClient();
	return usePagedList<ArtemisReport>({
		baseKey: ["reports", workspaceId],
		enabled: !!workspaceId,
		initialSort: [{ id: "createdAt", desc: true }],
		fetchPage: async ({ debouncedSearch, filters, sort, window }) => {
			const data = await gqlFetch(GetReportsDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisReportSortInput>(sort),
				where: buildWhere(debouncedSearch, ["title", "id"], filters) as
					| ArtemisReportFilterInput
					| undefined,
			});
			const c = data.reports;
			return {
				nodes: (c?.nodes as ArtemisReport[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

export function useReport(reportId: string) {
	const gqlFetch = useGqlClient();
	return useQuery({
		queryKey: ["report", reportId],
		queryFn: async () => {
			const data = await gqlFetch(GetReportDocument, { reportId });
			return data.report as ArtemisReportDetail | null;
		},
		enabled: !!reportId,
	});
}

export function useCreateReport() {
	const gqlFetch = useGqlClient();
	return useMutation({
		mutationFn: (input: CreateReportInput) =>
			gqlFetch(CreateReportDocument, { input }),
	});
}

export function useUpdateReport() {
	const gqlFetch = useGqlClient();
	return useMutation({
		mutationFn: ({
			reportId,
			input,
		}: {
			reportId: string;
			input: UpdateReportInput;
		}) => gqlFetch(UpdateReportDocument, { reportId, input }),
	});
}

export function useDeleteReports() {
	const gqlFetch = useGqlClient();
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (reportIds: string[]) => {
			await Promise.all(
				reportIds.map((reportId) =>
					gqlFetch(DeleteReportDocument, { reportId }),
				),
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["reports"] });
		},
	});
}
